"use client";

import React from "react";
import Link from "next/link";
import { 
  CheckCircle2, 
  HelpCircle, 
  Coins
} from "lucide-react";
import { Footer } from "@/components/LandingLayout";

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans antialiased transition-colors duration-300">

      <main className="container mx-auto px-4 max-w-5xl pt-20 pb-20">
        <header className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/10 text-blue-600 dark:text-blue-500 text-[10px] font-black uppercase tracking-widest mb-6">
            <Coins className="w-3 h-3" />
            Simple Pricing
          </div>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tight text-zinc-900 dark:text-white mb-4">Scale without friction.</h1>
          <p className="text-sm text-zinc-700 dark:text-zinc-400 max-w-xl mx-auto leading-relaxed font-medium">
            One simple model. No complicated tiers or hidden usage fees.
          </p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6 mb-24 items-stretch">
          {/* Free Tier */}
          <div className="p-8 bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-3xl flex flex-col shadow-sm dark:shadow-none">
            <div className="mb-8">
              <h3 className="text-xs font-bold text-zinc-600 dark:text-zinc-500 uppercase tracking-widest mb-2">Free Monthly</h3>
              <div className="text-3xl font-black text-zinc-900 dark:text-white">₹0</div>
              <p className="text-[10px] text-zinc-600 dark:text-zinc-500 mt-1 uppercase font-black">100 Requests Included</p>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                "100 Extraction Requests / Mo",
                "Reset on 1st of every month",
                "Full API Access",
                "Standard Support"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-zinc-800 dark:text-zinc-400 font-medium">
                  <CheckCircle2 className="w-3 h-3 text-zinc-300 dark:text-zinc-700" />
                  {item}
                </li>
              ))}
            </ul>
            <Link href="/sign-up-login-screen">
              <button className="w-full py-2.5 bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-white text-[11px] font-black uppercase tracking-widest rounded-xl hover:bg-zinc-300 dark:hover:bg-zinc-700 transition-all">
                Get Started
              </button>
            </Link>
          </div>

          {/* Pay-As-You-Go */}
          <div className="p-8 bg-blue-600 rounded-3xl flex flex-col shadow-2xl shadow-blue-600/20 scale-105 relative z-10">
            <div className="absolute top-4 right-8 bg-white/20 text-white text-[9px] font-black px-2 py-0.5 rounded uppercase">Featured</div>
            <div className="mb-8">
              <h3 className="text-xs font-bold text-blue-50 uppercase tracking-widest mb-2">Pay-As-You-Go</h3>
              <div className="text-3xl font-black text-white flex items-baseline gap-1">
                ₹0.003
                <span className="text-sm text-blue-100">/req</span>
              </div>
              <p className="text-[10px] text-blue-50 mt-1 uppercase font-black">Scalable Usage</p>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Unlimited Requests",
                "12 Requests / Sec Rate Limit",
                "Folder support included",
                "Direct Stream URLs",
                "Priority Support"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-white font-medium">
                  <CheckCircle2 className="w-3 h-3 text-blue-300" />
                  {item}
                </li>
              ))}
            </ul>
            <Link href="/sign-up-login-screen">
              <button className="w-full py-3 bg-white text-blue-600 text-[11px] font-black uppercase tracking-widest rounded-xl shadow-xl transition-all hover:scale-105 active:scale-95">
                Top Up Now
              </button>
            </Link>
          </div>

          {/* Enterprise */}
          <div className="p-8 bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-3xl flex flex-col shadow-sm dark:shadow-none">
            <div className="mb-8">
              <h3 className="text-xs font-bold text-zinc-600 dark:text-zinc-500 uppercase tracking-widest mb-2">Enterprise</h3>
              <div className="text-3xl font-black text-zinc-900 dark:text-white">Custom</div>
              <p className="text-[10px] text-zinc-600 dark:text-zinc-500 mt-1 uppercase font-black">High Volume</p>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Bulk Discounts",
                "Dedicated Infrastructure",
                "Custom Rate Limits",
                "Priority SLA"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-zinc-800 dark:text-zinc-400 font-medium">
                  <CheckCircle2 className="w-3 h-3 text-zinc-300 dark:text-zinc-700" />
                  {item}
                </li>
              ))}
            </ul>
            <a href="https://t.me/teraboxdl_api" target="_blank" rel="noopener noreferrer">
              <button className="w-full py-2.5 bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-white text-[11px] font-black uppercase tracking-widest rounded-xl hover:bg-zinc-300 dark:hover:bg-zinc-700 transition-all">
                Talk to Sales
              </button>
            </a>
          </div>
        </div>

        {/* Pricing FAQ */}
        <section className="max-w-3xl mx-auto border-t border-zinc-200 dark:border-zinc-800/30 pt-16">
          <h2 className="text-xl font-bold mb-10 text-center text-zinc-900 dark:text-white">Pricing FAQ</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { q: "How does the free quota work?", a: "Every account automatically receives 100 free requests per month. This resets on the 1st of every month." },
              { q: "Are there any minimums?", a: "No. You can add as little as ₹100 to your wallet." },
              { q: "Do failed requests cost money?", a: "No. Our system only deducts balance for successful (HTTP 200) extraction responses." }
            ].map((item, i) => (
              <div key={i}>
                <h4 className="text-sm font-bold mb-2 flex items-center gap-2 text-zinc-900 dark:text-white">
                  <HelpCircle className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-500" />
                  {item.q}
                </h4>
                <p className="text-xs text-zinc-700 dark:text-zinc-500 leading-relaxed pl-5 font-medium">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
